'use client';

import { type FC, Fragment, useCallback, useContext } from 'react';

import { VisuallyHidden } from '../visually-hidden';
import { PaginationRootContext } from './context/pagination-root.context';
import { usePaginationRange } from './hooks/use-pagination-range';
import { PaginationEllipsis } from './pagination-ellipsis';
import { PaginationItem } from './pagination-item';
import { PaginationPageButton } from './pagination-page-button';
import type { PaginationPageItemsPropsInterface } from './types/pagination-items.types';
import type { PaginationPageButtonPropsInterface } from './types/pagination-page-button.types';

/**
 * Generates and renders numbered `PageButton`s
 * @since 10.0.0
 */
export const PaginationPageItems: FC<PaginationPageItemsPropsInterface> = (props) => {
  const {
    renderItem = ({ key, buttonProps }) => (
      <PaginationItem key={key}>
        <PaginationPageButton {...buttonProps} />
      </PaginationItem>
    ),
  } = props;

  const {
    currentPageIndex = 0,
    totalPages,
    setPageIndex,
    showFirstLastButtons = false,
  } = useContext(PaginationRootContext);

  const render = useCallback(
    (pageIndex: number) => {
      const selected = currentPageIndex === pageIndex;
      const key = selected ? `selectedPageButton` : `page-${pageIndex}`;

      const buttonProps: PaginationPageButtonPropsInterface = {
        'selected': selected,
        'aria-current': selected ? 'page' : undefined,
        'children': (
          <Fragment>
            <VisuallyHidden>Page</VisuallyHidden> {pageIndex + 1}
          </Fragment>
        ),
        'onClick': () => setPageIndex?.(pageIndex),
      };

      return renderItem({ buttonProps, key, pageIndex });
    },
    [currentPageIndex, renderItem, setPageIndex],
  );

  const renderEllipsis = useCallback(
    (key: 'start' | 'end') => (
      <PaginationItem key={`ellipsis-${key}`}>
        <PaginationEllipsis />
      </PaginationItem>
    ),
    [],
  );

  const content = usePaginationRange({
    currentPageIndex,
    totalPages,
    /**
     * `showFirstLastButtons` uses a tighter range so the persistent first/last
     * anchors stay visually prominent: 3 pages in the middle layout (both
     * ellipses visible) and 5 pages in the larger group when only one
     * ellipsis is visible. The slot count stays at 7 across both layouts so
     * the component's width doesn't shift while navigating with prev/next.
     */
    maxNumOfPageButtons: showFirstLastButtons ? 6 : 7,
    showFirstLastButtons,
    render,
    renderEllipsis,
  });

  return <Fragment>{content}</Fragment>;
};

PaginationPageItems.displayName = 'Pagination.PageItems';
